import type { EntityOption } from '@/components/EntitySelect';
import { listMembers } from '@/features/members/api';
import { getProject, listProjects, projectMembersApi, workItemsApi } from './api';
import type { ProjectMember } from './types';

/** A project member's display name, or a "Member #<id>" placeholder when the API resolved none. */
export function projectMemberName(pm: Pick<ProjectMember, 'member' | 'member_name'>): string {
  return pm.member_name ?? `Member #${pm.member}`;
}

/** Resolve a work item's label for a preselected id (e.g. parent in edit mode). */
export async function resolveWorkItemLabel(value: string): Promise<string | null> {
  if (!value) return null;
  const item = await workItemsApi.get(Number(value));
  return `${item.identifier} ${item.title}`;
}

/** Work-item picker scoped to one project (e.g. choosing a parent).
 *  `excludeId` drops one item (e.g. self, for a parent picker); `stateGroups`
 *  restricts to those state rollup groups (e.g. the worklog picker → loggable
 *  items only). */
export function makeWorkItemSearch(
  projectId: number,
  opts: { excludeId?: number; stateGroups?: readonly string[] } = {},
) {
  return async (query: string): Promise<EntityOption[]> => {
    const page = await workItemsApi.list({
      project: projectId,
      search: query || undefined,
      state_group: opts.stateGroups?.length ? opts.stateGroups.join(',') : undefined,
      page_size: 20,
    });
    // Guard the scope client-side too: never offer another project's item, even
    // if the server ignored `project` (DEV13-76 — relations are same-project only).
    return page.results
      .filter((w) => w.project === projectId && w.id !== opts.excludeId)
      .map((w) => ({ value: String(w.id), label: `${w.identifier} ${w.title}` }));
  };
}

/**
 * Member picker keyed on the **Member id** (not user id) — the native Projects
 * FKs (`ProjectMember.member`, `Project.lead`, `WorkItem.assignees`) target
 * `users.Member`, unlike CRM owner fields which target `users.User`.
 */
/** Label a member — guests are tagged "(Client)" so staff can tell them apart
 *  from staff in shared pickers (assignees, project members, lead). */
function memberLabel(m: { user: { full_name: string }; type?: string }): string {
  return m.type === 'guest' ? `${m.user.full_name} (Client)` : m.user.full_name;
}

export async function searchMembersById(query: string): Promise<EntityOption[]> {
  const page = await listMembers({ search: query || undefined });
  return page.results.map((m) => ({ value: String(m.id), label: memberLabel(m) }));
}

export async function resolveMemberByIdLabel(value: string): Promise<string | null> {
  if (!value) return null;
  const page = await listMembers();
  const member = page.results.find((m) => String(m.id) === value);
  return member ? memberLabel(member) : null;
}

/**
 * Member picker scoped to ONE project's members (not the whole org roster) —
 * for FKs that must be a project member, e.g. `Module.lead`. Keyed on the
 * Member id (`ProjectMember.member`), matching the FK target. The member list
 * per project is small, so we page it and filter client-side.
 */
export function makeProjectMemberSearch(projectId: number) {
  return async (query: string): Promise<EntityOption[]> => {
    const page = await projectMembersApi.list({ project: projectId, page_size: 200 });
    const q = query.trim().toLowerCase();
    return page.results
      .map((pm) => ({
        value: String(pm.member),
        label: projectMemberName(pm),
      }))
      .filter((o) => !q || o.label.toLowerCase().includes(q));
  };
}

export function makeProjectMemberResolve(projectId: number) {
  return async (value: string): Promise<string | null> => {
    if (!value) return null;
    const page = await projectMembersApi.list({ project: projectId, page_size: 200 });
    const pm = page.results.find((m) => String(m.member) === value);
    return pm ? projectMemberName(pm) : null;
  };
}

/** Server search for the project picker (EntitySelect). */
export async function searchProjects(query: string): Promise<EntityOption[]> {
  const page = await listProjects({ search: query || undefined });
  return page.results.map((p) => ({ value: String(p.id), label: p.name }));
}

/** Resolve a project's name for a preselected id (edit mode). */
export async function resolveProjectLabel(value: string): Promise<string | null> {
  if (!value) return null;
  const project = await getProject(Number(value));
  return project.name;
}
